import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo, SilentRequest } from '@azure/msal-browser';
import { firstValueFrom } from 'rxjs';
import { EntraService } from '@k12/entra';

export interface UserProvisioningRequest {
  accountId: string;
  applicationName: string;
}

@Injectable({
  providedIn: 'root'
})
export class TokenValidationService {
  private readonly http = inject(HttpClient);
  private readonly msalService = inject(MsalService);
  private readonly entraService = inject(EntraService);

  /**
   * Checks if user has groups or roles in their token.
   * If not, provisions them via API and refreshes the token.
   */
  async validateAndProvisionToken(apiUrl: string, applicationName: string): Promise<boolean> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      console.warn('No active account found');
      return false;
    }

    // Content, not presence: `roles: []` is truthy, so testing the claim's existence sent users with
    // an empty claim down the provisioning path on every load.
    if (this.checkTokenHasGroupsOrRoles(account)) {
      return true;
    }

    // Only consult Graph when neither claim is present at all. A claim that is present but empty is
    // already conclusive -- the user genuinely holds nothing.
    if (!this.tokenCarriesGroupsOrRolesClaims(account)) {
      try {
        const userRoles = await this.entraService.getEntraUserGroups();
        if (userRoles.length > 0) {
          return true;
        }
      } catch (error) {
        // A Graph outage is not evidence the user is new. Provisioning on a transient failure is what
        // made this fire repeatedly for established users; the next app load retries.
        console.warn('Could not read Entra groups; skipping provisioning this cycle.', error);
        return false;
      }
    }

    try {
      await this.provisionUserRoles(apiUrl, account, applicationName);
      await this.refreshToken(account);
      return true;
    } catch (error) {
      console.error('Error during user provisioning:', error);
      return false;
    }
  }

  /** Whether Entra issued the claims at all, as opposed to issuing them empty. */
  private tokenCarriesGroupsOrRolesClaims(account: AccountInfo): boolean {
    const claims = account.idTokenClaims;
    return !!claims && (Array.isArray(claims['groups']) || Array.isArray(claims['roles']));
  }

  /**
   * Checks if the account has any groups or roles in the token claims
   */
  private checkTokenHasGroupsOrRoles(account: AccountInfo): boolean {
    const claims = account.idTokenClaims;
    if (!claims) return false;

    const groups = claims['groups'] as string[] | undefined;
    const roles = claims['roles'] as string[] | undefined;

    const hasGroups = Array.isArray(groups) && groups.length > 0;
    const hasRoles = Array.isArray(roles) && roles.length > 0;

    return hasGroups || hasRoles;
  }

  /**
   * Calls your API endpoint to provision roles/groups for the user
   */
  private async provisionUserRoles(apiUrl: string, account: AccountInfo, applicationName: string): Promise<void> {
    const payload: UserProvisioningRequest = {
      accountId: account.localAccountId || '',
      applicationName: applicationName || ''
    };

    // Replace with your actual API endpoint
    const endpoint = `${apiUrl}/account/assign-new-user-security`;

    await firstValueFrom(this.http.put(endpoint, payload));
  }

  /**
   * Forces a token refresh using silent authentication
   */
  private async refreshToken(account: AccountInfo): Promise<void> {
    const request: SilentRequest = {
      account: account,
      scopes: ['user.read'],
      forceRefresh: true // This forces a new token from the server
    };

    try {
      const response = await this.msalService.instance.acquireTokenSilent(request);

      // Update the active account with fresh token
      if (response.account) {
        this.msalService.instance.setActiveAccount(response.account);
      }
    } catch (error) {
      console.error('Silent token refresh failed:', error);
      // If silent refresh fails, you might need to do an interactive refresh
      throw error;
    }
  }
}
